<script>

import MintTokensModal from "@/components/modals/MintTokensModal.vue";
import {BIcon} from "buefy";
import FiltersPanel from "@/components/filters/FiltersPanel.vue";
import PerPageSelect from "@/components/ui/PerPageSelect.vue";

export default {
    components: {FiltersPanel, BIcon, MintTokensModal, PerPageSelect},
    computed: {
        filtersDef() {
            return [
                { key: 'company_name', label: this.$t('companies.fields.name.name'), type: 'text' },
                { key: 'amount', label: this.$t('token_purchases.fields.token_amount.name'), type: 'number' },
                { key: 'created_at', label: this.$t('token_purchases.fields.created_at.name'), type: 'date' },
                { key: 'status', label: this.$t('token_purchases.fields.status.name'), type: 'select',
                    options: [
                        { label: this.$t('token_purchases.statuses.waiting_for_transfer'), value: 'waiting_for_transfer' },
                        { label: this.$t('token_purchases.statuses.waiting_for_tokens'), value: 'waiting_for_tokens' },
                        { label: this.$t('token_purchases.statuses.completed'), value: 'completed' },
                        { label: this.$t('token_purchases.statuses.cancelled'), value: 'cancelled' },
                    ]
                },
            ]
        }
    },
    data() {
        return {
            loading: true,
            downloading: false,
            firstLoading: true,
            data: [],
            originalData: {},
            meta: {
                current_page: 1,
                total: 0,
                per_page: 0,
            },
            sort: {
                field: 'id',
                order: 'desc'
            },
            controller: new AbortController(),
            statusOptions: [
                'waiting_for_transfer',
                'waiting_for_tokens',
                'completed',
                'cancelled',
            ],
            selectedPurchase: null,
            filters: {},
            perPage: parseInt(localStorage.getItem('perPage_tokenBuys') || '10'),
        }
    },
    methods: {
        onFiltersChange(filters) {
            this.filters = filters;
            this.meta.current_page = 1;
            this.loadData();
        },
        loadData() {
            this.controller = new AbortController();
            this.loading = true;
            window.scrollTo({top: 0});
            axios.get('/token-purchases', {
                signal: this.controller.signal,
                params: {
                    page: this.meta.current_page,
                    per_page: this.perPage,
                    sort: this.sort.field,
                    order: this.sort.order,
                    filters: this.filters
                }
            }).then(response => {
                this.data = response.data.data;
                this.meta.current_page = response.data.meta.current_page;
                this.meta.total = response.data.meta.total;
                this.meta.per_page = response.data.meta.per_page;
            }).catch(() => {
            }).finally(() => {
                this.loading = false;
            })
        },
        download() {
            this.downloading = true;
            let params = {
                sort: this.sort.field,
                order: this.sort.order,
                filters: this.filters,
                xlsx: 1
            }
            axios({
                url: '/token-purchases',
                method: 'GET',
                responseType: 'blob',
                params
            }).then((response) => {
                saveAs(response.data, 'token-purchases.xlsx');
            }).finally(() => {
                this.downloading = false;
            });
        },
        onPageChange(page) {
            this.controller.abort();
            this.meta.current_page = page;
            this.loadData();
        },
        onSort(field, order) {
            this.controller.abort();
            this.sort = {field, order};
            this.loadData();
        },
        onPerPageChange() {
            localStorage.setItem('perPage_tokenBuys', String(this.perPage));
            this.meta.current_page = 1;
            this.loadData();
        },
        save(row) {
            row.saving = true;
            axios.put('/token-purchases/' + row.id, {
                token_amount: row.token_amount,
                status: row.status,
            }).then(response => {
                this.$toast.success(this.$t('messages.updated_successfully'));
                row.status_label = response.data.data.status_label;
                row.status = response.data.data.status;
                row.token_amount = response.data.data.token_amount;
            })
                .catch(() => {
                    this.$toast.error(this.$t('messages.error_try_again'))
                })
                .finally(() => {
                    row.editing = false;
                    row.saving = false;
                })
        },
        edit(row) {
            this.originalData[row.id] = {...row};
            row.editing = true;
        },
        cancelEditing(row) {
            row.status = this.originalData[row.id].status;
            row.status_label = this.originalData[row.id].status_label;
            row.token_amount = this.originalData[row.id].token_amount;
            row.editing = false;
        },
        openMintModal(row) {
            this.$modal.open();
            this.selectedPurchase = row;
        },
        closeMintModal() {
            this.$modal.close();
            this.selectedPurchase = null;
        },
        onMinted(purchase) {
            this.selectedPurchase.status = purchase.status;
            this.selectedPurchase.status_label = purchase.status_label;
            this.closeMintModal();
        },
        isMintButtonVisible(row) {
            return !row.editing && !row.saving && row.status === 'waiting_for_tokens';
        }
    },
    mounted() {
        this.loadData();
    },
    beforeUnmount() {
        this.controller.abort();
    }
}

</script>
<template>
    <section>

        <div class="mb-4">
            <FiltersPanel
                :filters="filtersDef"
                @update:activeFilters="onFiltersChange"
            >
                <template #buttons>
                    <b-button v-if="$auth.isAdmin()" :loading="downloading" type="is-link" icon-left="download" @click="download">
                        .xlsx
                    </b-button>
                </template>
            </FiltersPanel>
        </div>

        <MintTokensModal
            v-if="selectedPurchase"
            :token-purchase="selectedPurchase"
            v-on:minted="onMinted"
            v-on:close="closeMintModal"/>

        <b-table
            :data="loading ? [] : data"
            :loading="loading"

            :paginated="!loading"
            backend-pagination
            :total="loading ? 0 : meta.total"
            :per-page="perPage"
            @page-change="onPageChange"
            :aria-next-label="$t('navigation.next_page')"
            :aria-previous-label="$t('navigation.previous_page')"
            :aria-page-label="$t('navigation.page')"
            :aria-current-label="$t('navigation.current_page')"

            backend-sorting
            sort-icon="arrow-up"
            default-sort-direction="asc"
            :default-sort="['id', 'desc']"
            @sort="onSort">

            <b-table-column field="company_name" :label="$t('companies.fields.name.name')" v-slot="props">
                {{ props.row.company_name }}
            </b-table-column>

            <b-table-column field="nip" :label="$t('companies.fields.nip.name')" v-slot="props">
                {{ props.row.nip }}
            </b-table-column>

            <b-table-column
                field="token_amount"
                :label="$t('token_purchases.fields.token_amount.name')"
                v-slot="props">
                <span v-if="!props.row.editing">{{ props.row.token_amount }}</span>
                <b-input v-if="props.row.editing"
                         type="number"
                         v-model="props.row.token_amount"
                         :disabled="props.row.saving"/>
            </b-table-column>

            <b-table-column
                field="status"
                :label="$t('token_purchases.fields.status.name')"
                v-slot="props">
                <span v-if="!props.row.editing">{{ $t('token_purchases.statuses.' + props.row.status) }}</span>
                <b-select placeholder="$t('token_purchases.fields.status.placeholder')"
                          v-if="props.row.editing"
                          v-model="props.row.status"
                          :disabled="props.row.saving">
                    <option
                        v-for="status in statusOptions"
                        :value="status"
                        :key="status">
                        {{ $t('token_purchases.statuses.' + status) }}
                    </option>
                </b-select>
            </b-table-column>

            <b-table-column
                field="created_at"
                width="150"
                :label="$t('token_purchases.fields.created_at.name')"
                sortable
                v-slot="props">
                {{ props.row.created_at }}
            </b-table-column>

            <b-table-column field="" label="-" v-slot="props">

                <section class="buttons">

                    <b-button type="is-link" size="is-small"
                              :title="$t('actions.edit')"
                              @click="edit(props.row)"
                              icon-right="square-edit-outline"
                              v-if="!props.row.editing">
                    </b-button>

                    <b-button type="is-success" size="is-small"
                              :title="$t('actions.save')"
                              v-bind:disabled="props.row.saving"
                              @click="save(props.row)"
                              v-bind:class="{'is-loading': props.row.saving}"
                              icon-right="check"
                              v-if="props.row.editing">
                    </b-button>

                    <b-button size="is-small"
                              :title="$t('actions.cancel')"
                              v-bind:disabled="props.row.saving"
                              v-bind:class="{'is-loading': props.row.saving}"
                              @click="cancelEditing(props.row)"
                              icon-right="close"
                              v-if="props.row.editing">
                    </b-button>

                    <b-button type="is-small"
                              :class="{'is-primary': props.row.status === 'waiting_for_tokens'}"
                              :title="$t('token_purchases.labels.send_tokens')"
                              v-if="isMintButtonVisible(props.row)"
                              icon-right="cash-multiple"
                              @click="openMintModal(props.row)">
                    </b-button>

                </section>

            </b-table-column>

            <template #empty>
                <div v-if="!loading" class="block has-text-grey has-text-centered mb-4 mt-4">
                    {{ $t('token_purchases.labels.no_purchases') }}
                </div>
            </template>

            <template #bottom-left>
                <PerPageSelect v-model="perPage" @change="onPerPageChange" />
            </template>

        </b-table>

        <loader :loading="loading"></loader>
    </section>
</template>
